import { Observable, shareReplay } from 'rxjs';
import { IResourcePoolConfiguration } from '../interfaces/';

/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @summary `ResourcePool` takes in an `Observable` representing an asynchronous task, process, or resource
 *  and allows other concurrent consumers to join a single execution of the resource.
 */
export class ResourcePool<T = any> {

	public static readonly DEFAULT_THRESHOLD: number = 100;

	/** Set to true once when `close()` is called. */
	public closed: boolean;

	/** Unique string identifier for this `ResourcePool`.*/
	public id: string;

	/** The number of consumers in the pool. */
	public consumers: number;

	/** Whether or not the `ResourcePool` expires after a defined threshold.*/
	public expirable: boolean;

	/** Maximum number of consumers a `ResourcePool` can serve. `0` = unlimited consumers.*/
	public maxConsumers: number;

	/** The number of milliseconds each consumer leaves the next to join the pool before it closes.*/
	public threshold: number;

	/** Timestamp representing when the last consumer joined the pool.*/
	private _lastJoined: number;

	/** Asynchronous process/resource to be shared among multiple concurrent consumers.*/
	private _resource: Observable<T>;

	/** Optional callback to run when a `ResourcePool` closes.*/
	private _onClose?: () => void;

	/** Optional callback to run whenever a new consumer joins a `ResourcePool`.*/
	private _onJoin?: () => void;

	/** Optional callback to run when a consumer leaves a `ResourcePool`. Should only apply to
	 *  non-expirable `ResourcePool` instances. */
	private _onLeave?: () => void;

	constructor(_config: IResourcePoolConfiguration<T>) {
		this._pool(_config.resource);
		this.consumers = 0;
		this.id = _config.id || `ResourcePool_${Date.now()}`;
		this.maxConsumers = _config.maxConsumers && _config.maxConsumers >= 0 ? _config.maxConsumers : 0;

		if (_config.expirable === undefined)
			this.expirable = true;
		else
			this.expirable = !!_config.expirable;

		this.threshold = this.expirable ? (_config.threshold || ResourcePool.DEFAULT_THRESHOLD) : 0;
		this._onClose = _config.onClose;
		this._onJoin = _config.onJoin;
	}

	/** Checks if the threshold for joining the pool has passed.*/
	get expired(): boolean {
		if (!this.expirable || !this._lastJoined || !this.threshold)
			return false;

		return Date.now() - this._lastJoined > this.threshold;
	}

	get open(): boolean {
		if (this.closed)
			return false;

		if (this.expired)
			return false;

		if (
			this.maxConsumers &&
			this.consumers >= this.maxConsumers
		)
			return false;

		return true;
	}

	/** Joins a consumer to the pool so long as it's still open and extends the window for other concurrent consumers to join.*/
	public join(): Observable<T> | null {
		this._lastJoined = Date.now();
		this.consumers++;

		if (this._onJoin && typeof this._onJoin === 'function')
			this._onJoin();

		return this._resource;
	}

	/**
	 * Handles the event of a consumer leaving the `ResourcePool` and closes it
	 *  if there are no more consumers.
	 * @note This method should only be called when the `ResourcePool` does not expire,
	 *  the use case for which isn't clearly defined yet, and a `BehaviorSubject` is
	 *  probably a better option in the case of non-expiring resources anyway.
	 */
	public leave(): void {
		this.consumers--;
		if (this._onLeave && typeof this._onLeave === 'function')
			this._onLeave();

		if (this.consumers <= 0)
			this.close();
	}

	/**
	 * Destroys all instance data and runs optional callback.
	 */
	public close(): void {
		if (this._onClose && typeof this._onClose === 'function')
			this._onClose();

		this.consumers = null;
		this._lastJoined = null;
		this.closed = true;
	}

	/**
	 * Modifies the original resource to share its result with multiple consumers. Only called once during
	 *  `ResourcePool` initialization.
	 */
	private _pool(_resource: Observable<T>): void {
		// exit if resource has already been modified. ResourcePool should only ever be responsible
		// for a single resource. Create a new `ResourcePool` instance for a new resource instead.
		if (this._resource)
			return;

		this._resource = _resource.pipe(shareReplay(1));
	}

}
